import React from "react";
import { motion } from "framer-motion";
import { Shield, Target, Award, Users, History, TrendingUp } from "lucide-react";
import { IMAGES } from "@/assets/images";
import { companyStats } from "@/data/index";
import { StatsCard } from "@/components/Cards";
import { springPresets, fadeInUp, staggerContainer, staggerItem } from "@/lib/motion";

export default function Company() {
  return (
    <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative py-24 bg-primary text-primary-foreground overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src={IMAGES.ALUMINUM_PRODUCTS_3} 
            alt="Kurumsal Arka Plan" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springPresets.smooth}
            className="max-w-3xl"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6 italic-none">
              Geleceği Alüminyum ile Şekillendiriyoruz
            </h1>
            <p className="text-lg md:text-xl opacity-90 leading-relaxed">
              2020'den itibaren, Türkiye'nin ve dünyanın önde gelen projelerine
              yüksek teknolojili alüminyum çözümleri sunuyoruz. Entegre tesislerimizde
              hassasiyetle üretiyoruz.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Profile Section */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div 
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <h2 className="text-sm font-bold tracking-widest text-primary uppercase mb-4">
                Şirket Profili
              </h2>
              <h3 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
                Sektörde Güvenin ve Teknolojinin Adresi
              </h3>
              <div className="space-y-6 text-muted-foreground leading-relaxed">
                <p>
                  2020 yılında kurulan MIMALUM, alüminyum ekstrüzyon sektöründe güçlü ve teknoloji odaklı bir oyuncu olma vizyonuyla yola çıktı. Kısa sürede büyüyen tesislerimiz, 26.000 m² kapalı alanda 4 ekstrüzyon hattı, gelişmiş boyama tesisleri ve dökümhaneden nihai ürüne uzanan tam entegre bir üretim zincirini barındırmaktadır.
                </p>
                <p>
                  Mimari sistemlerden otomotive, enerjiden beyaz eşyaya kadar geniş bir yelpazede
                  çözüm ortağı olarak, her bir profili uluslararası kalite standartlarında (ISO, Qualicoat, Qualanod)
                  üretmekteyiz. İhracat odaklı vizyonumuzla 45'ten fazla ülkeye değer taşıyoruz.
                </p>
              </div>
              <div className="mt-10 grid grid-cols-2 gap-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-full">
                    <Shield className="w-5 h-5 text-primary" />
                  </div>
                  <span className="font-medium">%100 Kalite Kontrol</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-full">
                    <Award className="w-5 h-5 text-primary" />
                  </div>
                  <span className="font-medium">Global Sertifikalar</span>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={springPresets.gentle}
              className="relative"
            >
              <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src={IMAGES.EKRAN_G_R_NT_S_2026_02_07_202330_81} 
                alt="Üretim Tesisi" 
                className="w-full aspect-video lg:aspect-square object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl hidden md:block border border-border">
                <div className="flex items-center gap-4">
                  <TrendingUp className="w-10 h-10 text-primary" />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Yıllık Büyüme</p>
                    <p className="text-2xl font-bold text-primary">+15%</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Grid */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {companyStats.map((stat) => (
              <motion.div key={stat.id} variants={staggerItem}>
                <StatsCard stat={stat} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Vision & Values */}
      <section className="py-24 bg-background border-y border-border">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="space-y-4">
              <div className="w-12 h-12 bg-primary text-primary-foreground flex items-center justify-center rounded-lg">
                <Target className="w-6 h-6" />
              </div>
              <h4 className="text-xl font-bold">Vizyonumuz</h4>
              <p className="text-muted-foreground">
                Alüminyum teknolojilerinde global bir marka haline gelerek, sürdürülebilir 
                ve yenilikçi çözümlerle geleceğin dünyasını inşa etmek.
              </p>
            </div>
            <div className="space-y-4">
              <div className="w-12 h-12 bg-primary text-primary-foreground flex items-center justify-center rounded-lg">
                <Users className="w-6 h-6" />
              </div>
              <h4 className="text-xl font-bold">Misyonumuz</h4>
              <p className="text-muted-foreground">
                Müşteri beklentilerini en üst düzeyde karşılayan, çevreye duyarlı ve 
                mühendislik odaklı üretim süreçleriyle sektöre değer katmak.
              </p>
            </div>
            <div className="space-y-4">
              <div className="w-12 h-12 bg-primary text-primary-foreground flex items-center justify-center rounded-lg">
                <Award className="w-6 h-6" />
              </div>
              <h4 className="text-xl font-bold">Değerlerimiz</h4>
              <p className="text-muted-foreground">
                Dürüstlük, inovasyon, kalite tutkusu ve insana yatırım. Bizim için başarı 
                sadece rakamlar değil, yarattığımız güvendir.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* History Timeline */}
      <section className="py-24 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <History className="w-10 h-10 text-primary mx-auto mb-4 opacity-50" />
            <h2 className="text-3xl font-bold mb-4">Yolculuğumuz</h2>
            <p className="text-muted-foreground">
              Küçük bir atölyeden dev bir entegre tesise uzanan, başarı ve azim dolu 
              çeyrek asırlık hikayemiz.
            </p>
          </div>
          
          <div className="space-y-12 max-w-4xl mx-auto">
            {[ 
              { year: "2002", text: "İlk üretim tesisinin temelleri atıldı ve 4\" ekstrüzyon hattı devreye alındı." },
              { year: "2010", text: "Yıllık üretim kapasitesi 10.000 tona çıkarıldı, ilk ihracat Avrupa'ya gerçekleştirildi." },
              { year: "2018", text: "Tam entegre dikey toz boya tesisi ve 8\" yüksek kapasiteli hat yatırımları tamamlandı." },
              { year: "2026", text: "Dijital dönüşüm ve yeşil enerji entegrasyonu ile sektörün en modern tesislerinden biri haline gelindi." }
            ].map((item, index) => (
              <motion.div 
                key={index} 
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="flex gap-6 items-start"
              >
                <div className="text-2xl font-mono font-bold text-primary shrink-0 pt-1">{item.year}</div>
                <div className="pb-8 border-l-2 border-primary/20 pl-8">
                  <p className="text-lg text-foreground">{item.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>



      {/* CTA Section */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6 italic-none">Bizimle Tanışın</h2>
          <p className="mb-10 opacity-90 max-w-xl mx-auto">
            Tesislerimizi ziyaret etmek veya projeleriniz için teknik destek almak 
            isterseniz uzman ekibimiz her zaman yardıma hazır.
          </p>
          <button className="bg-white text-primary px-8 py-3 rounded-md font-bold hover:bg-opacity-90 transition-colors">
            İletişime Geçin
          </button>
        </div>
      </section>
    </div>
  );
}
